import { DatePipe } from "@angular/common";
import {
    Component,
    Input,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { InputComponent } from "@components/input/input.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextareaComponent } from "@components/textarea/textarea.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { TranslateService } from "@ngx-translate/core";
import { of } from "rxjs";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { CommandControlBinder } from "~app/common/services/cqs/command-control-binder/command-control-binder";
import { CommandService } from "~app/common/services/cqs/command.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { EditLeverantorsorderCommand } from "~app/features/forsaljning/leverantorsorder/commands/EditLeverantorsorderCommand";
import { ILeverantorsorderDetail } from "../../models/leverantorsorder-detail-model";
import { LeverantorsorderService } from "../../services/leverantorsorder.service";

@Component({
    selector: "rask-leverantorsorder-header",
    templateUrl: "./leverantorsorder-header.component.html",
    styleUrls: ["./leverantorsorder-header.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        TextmultilineComponent,
        InputComponent,
        RadiobuttonGroupComponent,
        TogglePanelComponent,
        SelectorComponent,
        TextareaComponent,
        DatePipe
    ]
})
export class LeverantorsorderHeaderComponent implements OnInit {
    form: UntypedFormGroup;
    @Input() orderDetail: ILeverantorsorderDetail;
    statusButtons = [
        { key: "Kontrakt",
            value: "Kontrakt" },
        { key: "Prognos",
            value: "Prognos" }
    ];
    @Input() texts: any;

    private readonly commandService = inject(CommandService);
    private readonly formBinder = inject(FormBinderService);
    private readonly levernatorsorderService = inject(LeverantorsorderService);
    private readonly translate = inject(TranslateService);

    get koppladeLeverantorer() {
        if (this.orderDetail.koppladeCKLeverantorer.length === 0) {
            return this.orderDetail.huvudkodInternnummer;
        }
        return `${this.orderDetail.huvudkodInternnummer} (${this.orderDetail.koppladeCKLeverantorer.join(", ")})`;
    }

    avtalAnsvarigSearch = (input: string) => this.levernatorsorderService.searchForAvtalsansvarigTypeahead({ query: input.trim() });

    avtalAnsvarigViewFormatter = (item: any) => item?.namn ?? item?.name;

    avtalTypeAheadDisabled = (item: ITypeAheadResult) => !item?.aktiv;

    avtalTypeAheadValueFormatter = (item: ITypeAheadResult) => item;

    bindControlsToCommands() {
        const cmdBinder = new CommandControlBinder(this.orderDetail.id, this.commandService, EditLeverantorsorderCommand);
        cmdBinder.bindControl(this.form.controls.avtalsansvarig, (v, c) => c.avtalansvarigId = v?.id);
        cmdBinder.bindControl(this.form.controls.status, (v, c) => c.status = v);
        cmdBinder.bindControl(this.form.controls.kontaktinformation, (v, c) => c.kontakt = v);
    }

    getPeriodFrom(): Date {
        return this.levernatorsorderService.getPeriodFrom(this.orderDetail.leverantorsorderrader);
    }

    getPeriodTom(): Date {
        return this.levernatorsorderService.getPeriodTom(this.orderDetail.leverantorsorderrader);
    }

    ngOnInit(): void {
        this.setupForm();
        this.bindControlsToCommands();
        this.setupTexts();
    }

    setAvtalsAnsvarig = (val) => of([val]);

    setupForm(): void {
        this.form = this.formBinder.CreateGroup(this.orderDetail);
    }

    setupTexts() {
        if (this.texts !== undefined) {
            return;
        }
        this.texts = {
            status: this.translate.instant("LEVERANTORSORDER_DETAIL_STATUS"),
            huvudkod: this.translate.instant("LEVERANTORSORDER_DETAIL_HUVUDKOD_INTERNNUMMER"),
            adress: this.translate.instant("LEVERANTORSORDER_DETAIL_BESOKSADRESS"),
            ordernummer: this.translate.instant("LEVERANTORSORDER_DETAIL_ORDERNUMMER"),
            from: this.translate.instant("LEVERANTORSORDER_DETAIL_FROM"),
            tom: this.translate.instant("LEVERANTORSORDER_DETAIL_TOM"),
            ansvarig: this.translate.instant("LEVERANTORSORDER_DETAIL_AVTALANSVARIG"),
            kontakt: this.translate.instant("LEVERANTORSORDER_DETAIL_KONTAKTTILLLEV"),
            typeaheadText: this.translate.instant("DEFAULT_TYPE_AHEAD_HELP_TEXT"),
            updatedBy: this.translate.instant("LEVERANTORSORDER_DETAIL_UBY"),
            createdBy: this.translate.instant("LEVERANTORSORDER_DETAIL_IBY")
        };
    }
}
